import { unwrap } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, CodeBlock, Icon, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, type BadgeTone } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceId } from "../../../session/workspace-context";
import { buildKeys } from "../shared/keys";
import type { Trace } from "../shared/types";
import { traceStatus, type TraceStatus } from "./trace-model";
import { AccessSection, ChunksSection, GroundingSection, LatencySection, QuerySection, TokensSection } from "./trace-sections";

const STATUS_TONE: Readonly<Record<TraceStatus, BadgeTone>> = { idle: "neutral", running: "brand", grounded: "success", fallback: "warning", error: "danger" };
const JSON_INDENT = 2;

export function useTrace(messageId: string | null, busy: boolean) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: buildKeys.trace(ws, messageId ?? ""),
    queryFn: () => unwrap(api.GET("/v1/messages/{message_id}/trace", { params: { path: { message_id: messageId ?? "" } } })),
    enabled: Boolean(messageId) && !busy,
    retry: 1,
  });
}

function Empty({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <div className="empty py-16">
      <span className="inline-flex size-11 items-center justify-center rounded-pill bg-sunken text-fg-subtle"><Icon name="activity" className="size-5" /></span>
      <div className="text-sm font-bold text-fg">{title}</div>
      <p className="max-w-64 text-xs">{t("build.playground.trace.emptyBody")}</p>
    </div>
  );
}

function SourcesUsed({ trace }: { trace: Trace }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const used = trace.chunks.filter((c) => c.used);
  if (used.length === 0) {
    return (
      <div className="empty py-16">
        <Icon name="help-circle" className="size-6 text-accent" />
        <div className="text-sm font-bold text-fg">{t("build.playground.trace.noSourcesTitle")}</div>
        <p className="max-w-64 text-xs">{t("build.playground.trace.noSourcesBody")}</p>
        <Button asChild variant="secondary" size="sm"><AppLink href={consoleRoutes.gaps(summary.slug)}>{t("build.playground.trace.answerGap")}</AppLink></Button>
      </div>
    );
  }
  return (
    <ul className="space-y-3 p-4">
      {used.map((chunk, i) => (
        <li key={chunk.chunk_id} className="rounded-lg border border-line p-3">
          <div className="flex items-center gap-2">
            <span className="cite">{i + 1}</span>
            <Icon name="file-text" className="size-4 text-primary-ink" />
            {chunk.source_id ? (
              <AppLink href={consoleRoutes.sourceDetail(summary.slug, chunk.source_id)} className="min-w-0 flex-1 truncate text-sm font-bold hover:text-primary-ink">{chunk.title ?? t("build.playground.trace.untitled")}</AppLink>
            ) : <span className="min-w-0 flex-1 truncate text-sm font-bold">{chunk.title ?? t("build.playground.trace.untitled")}</span>}
          </div>
          <div className="mt-1 text-[11px] text-fg-subtle">{t("build.playground.trace.ranks.rerank")} {chunk.rerank_score?.toFixed(2) ?? "—"}</div>
        </li>
      ))}
    </ul>
  );
}

interface TracePanelProps {
  messageId: string | null;
  busy: boolean;
  /** Extra state shown in the raw JSON (audience simulated, model selected). */
  context: Record<string, unknown>;
}

/** "Retrieval trace" aside: Trace | Sources used | Raw JSON for the latest answer. */
export function TracePanel({ messageId, busy, context }: TracePanelProps) {
  const { t } = useTranslation();
  const trace = useTrace(messageId, busy);
  const status = traceStatus({ busy, trace: trace.data, failed: trace.isError, hasMessage: Boolean(messageId) });
  const data = trace.data;
  const body = (content: (trace: Trace) => React.ReactNode, emptyTitle: string) => {
    if (busy) return <p className="flex items-center gap-2 p-4 text-xs text-fg-subtle" role="status"><span className="typing-dot" />{t("build.playground.trace.running")}</p>;
    if (trace.isError) return <p className="p-4 text-xs text-danger" role="alert">{t("build.playground.trace.loadError")}</p>;
    if (messageId && trace.isPending) return <div className="space-y-3 p-4" aria-hidden="true">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>;
    return data ? content(data) : <Empty title={emptyTitle} />;
  };
  return (
    <Tabs defaultValue="trace" className="flex min-h-0 flex-1 flex-col" data-testid="trace-panel">
      <div className="shrink-0 border-b border-line px-4 pt-3">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-bold"><Icon name="activity" className="size-4 text-fg-subtle" />{t("build.playground.trace.title")}</h2>
          <Badge tone={STATUS_TONE[status]} dot data-testid="trace-status">{t(`build.playground.trace.status.${status}`)}</Badge>
        </div>
        <TabsList className="mt-2 border-b-0">
          <TabsTrigger value="trace">{t("build.playground.trace.tabs.trace")}</TabsTrigger>
          <TabsTrigger value="sources">{t("build.playground.trace.tabs.sources")}</TabsTrigger>
          <TabsTrigger value="json">{t("build.playground.trace.tabs.json")}</TabsTrigger>
        </TabsList>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto" aria-live="polite">
        <TabsContent value="trace">
          {body((tr) => (
            <>
              <QuerySection trace={tr} />
              <LatencySection trace={tr} />
              <AccessSection trace={tr} />
              <ChunksSection trace={tr} />
              <TokensSection trace={tr} />
              <GroundingSection trace={tr} />
            </>
          ), t("build.playground.trace.emptyTitle"))}
        </TabsContent>
        <TabsContent value="sources">{body((tr) => <SourcesUsed trace={tr} />, t("build.playground.trace.noSources"))}</TabsContent>
        <TabsContent value="json">
          {body((tr) => (
            <div className="p-4">
              <CodeBlock label={t("build.playground.trace.endpoint", { id: tr.message_id.slice(0, 8) })} code={JSON.stringify({ ...context, ...tr }, null, JSON_INDENT)} copyLabel={t("build.playground.trace.copy")} copiedLabel={t("build.playground.trace.copied")} />
            </div>
          ), t("build.playground.trace.nothing"))}
        </TabsContent>
      </div>
    </Tabs>
  );
}
